import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import { addDays, format } from "date-fns";
import React, { useEffect, useState } from "react";
import { API_RESULT, API_VERSION } from "../../../../enums/common.enum";
import { instance } from "../../../../utils/api";
import {
  amountConverter,
  getMuiTheme,
  handleError,
} from "../../../../utils/common";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles"; // ✅ FIXED
import { useParams } from "react-router-dom";
import { DateRangePicker } from "mui-daterange-picker";
import { dateRangeMenu } from "../../../../utils/dateRangeMenu";
import { commonTableOptions } from "../../../../constants";

const MerchantRewards = ({
  providerInfo,
  dateRangeTxn,
  setDateRangeTxn,
  data,
  setData,
  visitedTabs,
  updateVisitedTabs,
}: any) => {
  const [loading, setLoading] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const params = useParams();

  const toggle = () => setOpenPicker(!openPicker);

  useEffect(() => {
    const getRewardsData = async () => {
      let payload = {
        start_date: format(dateRangeTxn?.startDate as Date, "yyyy-MM-dd"),
        end_date: format(dateRangeTxn?.endDate as Date, "yyyy-MM-dd"),
        metric_type: "rewards",
        location_id: params?.locationId,
      };
      setLoading(true);
      try {
        const result = await instance.post(
          `${API_VERSION.V2}/analytics/digital-payments/overview`,
          payload,
        );
        if (result.data?.status === API_RESULT.SUCCESS) {
          updateVisitedTabs(7, "add");
          setData(result.data.response);
          setOpenPicker(false);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    if (!visitedTabs.includes(6)) {
      getRewardsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeTxn]);

  const cols = [
    {
      name: "issued_at",
      label: "Issued At",
      options: {
        sort: true,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value ? format(new Date(value), "dd/MM/yyyy") : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "offer_type",
      label: "Offer Type",
      options: {
        searchable: false,
        sort: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "offer_title",
      label: "Offer Title",
      options: {
        searchable: false,
        sort: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value && value.trim() !== "" ? value : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "offer_description",
      label: "Offer Description",
      options: {
        searchable: false,
        sort: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value && value.trim() !== "" ? value : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "voucher_info",
      label: "Voucher",
      options: {
        searchable: false,
        sort: false,
        customBodyRender: (_value: any, tableMeta: any) => {
          const rowIndex = tableMeta?.rowIndex;
          const row = (data || [])[rowIndex]; // 👈 access original row object

          const voucherCode =
            row?.voucher_code && row?.voucher_code.trim() !== ""
              ? row.voucher_code
              : "-";
          const voucherPartner =
            row?.voucher_partner && row?.voucher_partner.trim() !== ""
              ? row.voucher_partner
              : "-";

          if (voucherCode === "-" && voucherPartner === "-") {
            return <Typography variant="body2">-</Typography>;
          }

          return (
            <Typography variant="body2">
              {`${voucherCode} (${voucherPartner})`}
            </Typography>
          );
        },
      },
    },
    {
      name: "amount",
      label: "Amount",
      options: {
        filter: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value && !isNaN(value) ? `₹ ${value}` : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        searchable: false,
        sort: false,
        customBodyRender: (value: any) => (
          <Chip
            size="small"
            label={value || "-"}
            sx={{
              backgroundColor:
                value === "scratched"
                  ? "#C6F6D5"
                  : value === "new"
                    ? "FBD38D"
                    : "#C53030",
              padding: "6px",
              color: value === "failed" ? "#fff" : "#000",
            }}
          />
        ),
      },
    },
    {
      name: "scratched_at",
      label: "Scratched At",
      options: {
        filter: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value ? format(new Date(value), "dd/MM/yyyy HH:mm:ss") : "-"}
          </Typography>
        ),
      },
    },
    {
      name: "expires_at",
      label: "Expires At",
      options: {
        filter: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">
            {value ? format(new Date(value), "dd/MM/yyyy") : "-"}
          </Typography>
        ),
      },
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Terminal ID",
    sort: true,
    filter: false,
    rowsPerPageOptions: [10, 50, 100, 200],
    rowsPerPage: 10,
  };

  return (
    <div>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Stack>
          <TextField
            sx={{ minWidth: "275px" }}
            fullWidth
            id="date_range"
            label={dateRangeTxn?.label}
            size="small"
            value={`${format(
              dateRangeTxn?.startDate as Date,
              "dd/MM/yyyy",
            )} - ${format(dateRangeTxn?.endDate as Date, "dd/MM/yyyy")}`}
            onFocus={toggle}
          />
          <Box sx={{ position: "absolute", zIndex: 1000 }}>
            <DateRangePicker
              open={openPicker}
              toggle={toggle}
              onChange={(range) => {
                updateVisitedTabs(6, "remove");
                setDateRangeTxn(range);
              }}
              minDate={new Date("2024-04-01")} // ✅ FIXED
              maxDate={addDays(new Date(), 1)}
              definedRanges={dateRangeMenu}
            />
          </Box>
        </Stack>

        {/* Count & Amount Summary */}
        <Box
          width={{ xs: "100%", md: "28%" }}
          sx={{
            boxShadow: "0 0 .25rem rgba(0,0,0,.075)",
            padding: "8px",
            borderRadius: "5px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            ml: "auto", // ✅ pushes box to the right
          }}
        >
          {/* Cash Summary */}
          <Box sx={{ textAlign: "center", flex: 1 }} px={1}>
            <Typography sx={{ fontSize: "11px !important" }}>
              Cash Count
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "16px !important" }}>
              <strong>
                {
                  (data || []).filter(
                    (item: any) => item.offer_type?.toLowerCase() === "cash",
                  ).length
                }
              </strong>
            </Typography>

            <Typography sx={{ fontSize: "11px !important", mt: 0.5 }}>
              Cash Amount
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "16px !important" }}>
              ₹&nbsp;
              {amountConverter(
                (data || [])
                  .filter(
                    (item: any) => item.offer_type?.toLowerCase() === "cash",
                  )
                  .reduce(
                    (acc: number, curr: any) => acc + Number(curr.amount ?? 0),
                    0,
                  ),
              )}
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{ width: "1px", backgroundColor: "#e7e7e7", height: "100%" }}
          />

          {/* Voucher Summary */}
          <Box sx={{ textAlign: "center", flex: 1 }} px={1}>
            <Typography sx={{ fontSize: "11px !important" }}>
              Voucher Count
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "16px !important" }}>
              <strong>
                {
                  (data || []).filter(
                    (item: any) => item.offer_type?.toLowerCase() === "voucher",
                  ).length
                }
              </strong>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Data Table */}
      <Box mt={3}>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title=""
            data={data || []} // ✅ FIXED
            columns={cols}
            options={options}
          />
        </ThemeProvider>
      </Box>
    </div>
  );
};

export default MerchantRewards;
